import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { UNAUTHORIZED_EVENT } from "../http";
import { useAuth } from "./AuthContext";
import { maakFoutmelding } from "../utils/foutmelding";
import { kiesMap } from "../components/dossier/kiesMap";
import { planCdZip } from "../components/dossier/cdZip";
import {
  haalUploadBatch,
  maakUploadBatch,
  NieuwBatchBestand,
  stopUploadBatch,
  werkBestandBij,
} from "../services/uploadBatchService";
import { FoutMetSessie, verwerkUploadItem } from "./uploadItem";
import {
  alsOnderbroken,
  HARTSLAG_GELDIG_MS,
  HARTSLAG_INTERVAL_MS,
  hartslagVanDitTabblad,
  koppelAanGekozenMap,
  leesOpgeslagenBatches,
  looptElders,
  onthoudBatch,
  ruimOudeHartslagenOp,
  vergeetBatch,
  wisEigenHartslag,
  zetHartslag,
} from "./uploadHervatten";
import {
  ActieveBatch,
  Baan,
  BANEN,
  baanVoor,
  grootteVan,
  isBezig,
  jsonBytes,
  LEGE_WACHTRIJ_STATE,
  MAX_BESTANDEN_PER_BATCH,
  OnderbrokenBatch,
  QueuedUploadItem,
  UploadCompletedInfo,
  UploadFout,
  uploadQueueReducer,
  verdeelInBatches,
  wijzigdatumVan,
} from "./uploadQueueReducer";

export type {
  Baan,
  OnderbrokenBatch,
  QueuedUploadItem,
  UploadCompletedInfo,
} from "./uploadQueueReducer";

/** Voortgang van een lopende batch, zoals de tracker die toont. */
export interface UploadQueueState {
  baan: Baan;
  dossierKenmerk: string;
  done: number;
  total: number;
  current: string | null;
  /** 0..1 van het lopende bestand, voor een vloeiende voortgangsring. */
  currentFraction: number;
  errors: UploadFout[];
}

/** Eén wachtende batch, zoals de tracker die opsomt. */
export interface WachtendeBatchInfo {
  id: number;
  baan: Baan;
  dossierKenmerk: string;
  aantal: number;
}

interface UploadQueueContextValue {
  /** De lopende batches: hooguit één per baan, dus hooguit twee. */
  lopend: UploadQueueState[];
  wachtrij: WachtendeBatchInfo[];
  /** Sessie verlopen: de uploads staan stil tot er weer is ingelogd. */
  gepauzeerd: boolean;
  lastCompleted: UploadCompletedInfo | null;
  /** Batches in het register die niet af zijn en hier niet lopen (na herladen of een fout). */
  onderbroken: OnderbrokenBatch[];
  /** Laat de map opnieuw kiezen en gaat verder waar de upload was. */
  hervatOnderbroken: (batchId: string) => Promise<void>;
  /** Stopt een onderbroken batch; wat al binnen is blijft. */
  stopOnderbroken: (batchId: string) => Promise<void>;
  /**
   * Zet een batch in de wachtrij van zijn baan. "start" = loopt direct,
   * "wacht" = er loopt al een batch in die baan, false = lege batch.
   */
  startBatchUpload: (
    dossierKenmerk: string,
    items: QueuedUploadItem[],
  ) => false | "start" | "wacht";
}

const UploadQueueContext = createContext<UploadQueueContextValue | undefined>(
  undefined,
);

/** Poll-interval voor "is er weer een sessie?" terwijl de uploads stilstaan. */
const SESSIE_POLL_MS = 10_000;

/** Een batch zoals het uploadregister hem kent. */
interface Registratie {
  batchId: string;
  aangemaaktOp: string;
  bestandIds: string[];
}

const naarNieuwBestand = (item: QueuedUploadItem): NieuwBatchBestand => ({
  relatiefPad: item.relatiefPad ?? item.naam,
  naam: item.naam,
  grootte: grootteVan(item),
  laatstGewijzigdOp: wijzigdatumVan(item),
  category: item.category,
  folderPath: item.targetFolder,
  cdZip: "cd" in item,
});

const httpStatus = (err: unknown) =>
  (err as { response?: { status?: number } })?.response?.status;

/**
 * App-brede achtergrond-upload: de lussen leven hier (niet in
 * DocumentenSection) zodat de voortgangs-FAB en de upload zelf navigatie
 * binnen de webapp overleven. Elke batch staat meteen in het uploadregister
 * (I18, WP3); na herladen, slaap of opnieuw inloggen vraagt de FAB de map
 * opnieuw te kiezen en gaat de upload verder waar hij was. Het sluiten van het
 * tabblad stopt de bytes wel; daarvoor waarschuwt de browser zolang er iets
 * loopt. Twee banen lopen naast elkaar (zie uploadQueueReducer).
 */
export const UploadQueueProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(uploadQueueReducer, LEGE_WACHTRIJ_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;
  const { user, vernieuwSessie } = useAuth();
  const volgendeIdRef = useRef(1);
  // Id van de batch waarvan de lus per baan al loopt: het effect hieronder mag
  // dezelfde batch nooit twee keer starten (StrictMode draait effecten dubbel,
  // en elke voortgangsstap wijzigt het actief-object).
  const lopendeIdRef = useRef<Record<Baan, number | null>>({
    groot: null,
    normaal: null,
  });
  // Lussen die op een nieuwe sessie wachten; HERVAT laat ze verder gaan.
  const hervattersRef = useRef<(() => void)[]>([]);

  const wachtOpNieuweSessie = useCallback(
    () =>
      new Promise<void>((resolve) => {
        hervattersRef.current.push(resolve);
        dispatch({ type: "PAUZE" });
      }),
    [],
  );

  const registratiesRef = useRef(new Map<number, Promise<Registratie>>());
  // De laatst gestarte registratie: de volgende wacht daarop (L6).
  const vorigeRegistratieRef = useRef<Promise<unknown>>(Promise.resolve());
  // Register-ids die in dit tabblad lopen of wachten: die zijn niet "onderbroken".
  const serverIdsRef = useRef(new Set<string>());

  // Bezit en hartslag horen bij elkaar (B1b-1): zolang een id in serverIdsRef
  // staat (geregistreerd, wachtend of lopend), houdt dit tabblad zijn hartslag
  // bij, zodat andere tabbladen hem niet als onderbroken tonen.
  const neemOver = useCallback((batchId: string) => {
    serverIdsRef.current.add(batchId);
    zetHartslag(batchId);
  }, []);
  const laatLos = useCallback((batchId: string) => {
    serverIdsRef.current.delete(batchId);
    wisEigenHartslag(batchId);
  }, []);
  /**
   * Ververst de hartslag van een eigen batch, tenzij een ander tabblad hem
   * intussen vers heeft (na slaap, bevriezing of een lange afremming nam dat
   * tabblad hem over): dan is hij verloren. Niet overschrijven, uit bezit
   * halen; draaiBatch stopt vóór het volgende bestand (B1c-1).
   */
  const verversOfVerlies = useCallback((batchId: string): boolean => {
    if (looptElders(batchId)) {
      serverIdsRef.current.delete(batchId);
      return false;
    }
    zetHartslag(batchId);
    return true;
  }, []);
  useEffect(() => {
    ruimOudeHartslagenOp();
    const tik = window.setInterval(() => {
      Array.from(serverIdsRef.current).forEach((id) => verversOfVerlies(id));
    }, HARTSLAG_INTERVAL_MS);
    // Herladen of sluiten: de batches meteen vrijgeven voor andere tabbladen.
    const weg = () =>
      serverIdsRef.current.forEach((id) => wisEigenHartslag(id));
    window.addEventListener("pagehide", weg);
    // Terug uit de back/forward-cache: pagehide wiste de hartslagen, maar de
    // batches zijn nog van dit tabblad. Meteen weer schrijven, met dezelfde
    // regel: heeft een ander tabblad hem intussen vers, dan verloren (B1c-2).
    const terug = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      Array.from(serverIdsRef.current).forEach((id) => verversOfVerlies(id));
    };
    window.addEventListener("pageshow", terug);
    return () => {
      window.clearInterval(tik);
      window.removeEventListener("pagehide", weg);
      window.removeEventListener("pageshow", terug);
    };
  }, [verversOfVerlies]);

  const metSessie = useCallback(
    async <T,>(verzoek: () => PromiseLike<T>): Promise<T> => {
      for (;;) {
        try {
          return await verzoek();
        } catch (err) {
          if (httpStatus(err) !== 401) throw err;
          await wachtOpNieuweSessie();
        }
      }
    },
    [wachtOpNieuweSessie],
  );

  const registreer = useCallback(
    async (
      dossierKenmerk: string,
      items: QueuedUploadItem[],
    ): Promise<Registratie> => {
      const a = await metSessie(() =>
        maakUploadBatch(dossierKenmerk, items.map(naarNieuwBestand)),
      );
      const bestandIds = a.bestanden.map((b) => b.id);
      // Eerst de hartslag, dan de wijzer: zo ziet een ander tabblad de batch
      // nooit zonder hartslag (B1b-1).
      neemOver(a.batchId);
      onthoudBatch(dossierKenmerk, a.batchId, bestandIds);
      return { batchId: a.batchId, aangemaaktOp: a.aangemaaktOp, bestandIds };
    },
    [metSessie, neemOver],
  );

  // Nog eens kijken zodra de hartslag van een ander tabblad verlopen kan zijn.
  const hercontroleRef = useRef<number | null>(null);
  const zoekOnderbrokenRef = useRef<() => Promise<void>>(async () => undefined);

  const zoekOnderbroken = useCallback(async () => {
    const lijst: OnderbrokenBatch[] = [];
    let elders = false;
    ruimOudeHartslagenOp();
    for (const { dossierKenmerk, batchId } of leesOpgeslagenBatches()) {
      if (serverIdsRef.current.has(batchId)) continue;
      // Een ander tabblad uploadt deze batch nog: hier niet als onderbroken
      // tonen, anders hervatten twee tabbladen dezelfde sessie (B1-1).
      if (looptElders(batchId)) {
        elders = true;
        continue;
      }
      try {
        const o = alsOnderbroken(
          dossierKenmerk,
          await haalUploadBatch(dossierKenmerk, batchId),
        );
        if (o) lijst.push(o);
        else {
          vergeetBatch(dossierKenmerk, batchId);
          wisEigenHartslag(batchId);
        }
      } catch (err) {
        // Weg, van een ander account, of geen toegang meer: niets te hervatten.
        if ([400, 403, 404].includes(httpStatus(err) ?? 0)) {
          vergeetBatch(dossierKenmerk, batchId);
          wisEigenHartslag(batchId);
        }
      }
    }
    dispatch({ type: "ONDERBROKEN", lijst });
    if (hercontroleRef.current !== null) {
      window.clearTimeout(hercontroleRef.current);
      hercontroleRef.current = null;
    }
    if (elders) {
      // Is dat tabblad weg (gesloten, vastgelopen), dan verloopt zijn
      // hartslag en verschijnt de batch hier alsnog.
      hercontroleRef.current = window.setTimeout(() => {
        hercontroleRef.current = null;
        void zoekOnderbrokenRef.current();
      }, HARTSLAG_GELDIG_MS);
    }
  }, []);
  zoekOnderbrokenRef.current = zoekOnderbroken;
  useEffect(
    () => () => {
      if (hercontroleRef.current !== null) {
        window.clearTimeout(hercontroleRef.current);
      }
    },
    [],
  );

  const startBatchUpload = useCallback(
    (
      dossierKenmerk: string,
      items: QueuedUploadItem[],
    ): false | "start" | "wacht" => {
      if (items.length === 0) return false;
      const wacht = stateRef.current.banen[baanVoor(items)].actief !== null;
      // Per stuk hooguit 200 bestanden én 60 KB JSON (F2).
      const delen = verdeelInBatches(items, MAX_BESTANDEN_PER_BATCH, (i) =>
        jsonBytes(naarNieuwBestand(i)),
      );
      for (const deel of delen) {
        const id = volgendeIdRef.current++;
        // Meteen vastleggen, ook als de batch nog moet wachten: zo overleeft
        // de wachtrij een herlaadbeurt (incident 11-10-2026). Na elkaar, niet
        // tegelijk: elke registratie is een transactie met tot 200 inserts (L6).
        const registratie = vorigeRegistratieRef.current
          .catch(() => undefined)
          .then(() => registreer(dossierKenmerk, deel));
        vorigeRegistratieRef.current = registratie;
        registratie.catch(() => undefined); // de fout komt in draaiBatch
        registratiesRef.current.set(id, registratie);
        dispatch({
          type: "ENQUEUE",
          batch: { id, dossierKenmerk, items: deel },
        });
      }
      return wacht ? "wacht" : "start";
    },
    [registreer],
  );

  const draaiBatch = useCallback(
    async (baan: Baan, batch: ActieveBatch) => {
      let reg: Registratie;
      try {
        reg = batch.batchId
          ? {
              batchId: batch.batchId,
              aangemaaktOp: batch.aangemaaktOp ?? new Date().toISOString(),
              bestandIds: batch.items.map((i) => i.bestandId ?? ""),
            }
          : await (registratiesRef.current.get(batch.id) ??
              registreer(batch.dossierKenmerk, batch.items));
      } catch (err) {
        // Niet in het register: er is niets geüpload. Eén melding voor de
        // hele batch, niet een fout per bestand (trein 51, L4).
        const n = batch.items.length;
        dispatch({
          type: "ITEM_KLAAR",
          baan,
          aantal: n,
          fout: {
            naam: n === 1 ? batch.items[0].naam : `${n} bestanden`,
            melding: maakFoutmelding(err),
            aantal: n,
          },
        });
        dispatch({ type: "BATCH_KLAAR", baan, at: Date.now() });
        return;
      } finally {
        registratiesRef.current.delete(batch.id);
      }
      // Ook de voortgang ververst de hartslag, hooguit eens per 15 s: Chrome
      // remt de timers van een verborgen tabblad af, netwerkantwoorden niet (B1b-3).
      let laatsteSlag = Date.now();
      const verversHartslag = () => {
        if (Date.now() - laatsteSlag < HARTSLAG_INTERVAL_MS) return;
        laatsteSlag = Date.now();
        verversOfVerlies(reg.batchId);
      };
      for (const [i, item] of batch.items.entries()) {
        // Verloren aan een ander tabblad: dat gaat verder, hier niet (B1c-1).
        if (!serverIdsRef.current.has(reg.batchId)) break;
        try {
          await verwerkUploadItem(
            {
              dossierKenmerk: batch.dossierKenmerk,
              batchId: reg.batchId,
              aangemaaktOp: reg.aangemaaktOp,
              metSessie,
              onProgress: (p) => {
                verversHartslag();
                dispatch({
                  type: "VOORTGANG",
                  baan,
                  fraction: (p.percentage ?? 0) / 100,
                });
              },
            },
            item,
            item.bestandId ?? reg.bestandIds[i],
          );
          dispatch({ type: "ITEM_KLAAR", baan });
        } catch (err) {
          const metSessie = err instanceof FoutMetSessie;
          const oorzaak = metSessie ? err.oorzaak : err;
          // Intussen door een ander tabblad overgenomen en het bestand had een
          // uploadsessie: dat tabblad maakt het af, dus hier geen fout
          // (B1c-low). Zonder sessie staat het op MISLUKT en neemt dat tabblad
          // het niet over: dan blijft de fout hier staan (B3, review 3).
          const verloren =
            !serverIdsRef.current.has(reg.batchId) ||
            !verversOfVerlies(reg.batchId);
          if (verloren && metSessie) {
            dispatch({ type: "ITEM_KLAAR", baan });
            continue;
          }
          console.error("[UploadQueue] Upload mislukt", {
            batchId: reg.batchId,
            err: oorzaak,
          });
          dispatch({
            type: "ITEM_KLAAR",
            baan,
            fout: { naam: item.naam, melding: maakFoutmelding(oorzaak) },
          });
        }
      }
      dispatch({
        type: "BATCH_KLAAR",
        baan,
        at: Date.now(),
        elders: !serverIdsRef.current.has(reg.batchId),
      });
      laatLos(reg.batchId);
      void zoekOnderbroken();
    },
    [metSessie, registreer, zoekOnderbroken, laatLos, verversOfVerlies],
  );

  const actiefGroot = state.banen.groot.actief;
  const actiefNormaal = state.banen.normaal.actief;
  useEffect(() => {
    const paren: [Baan, ActieveBatch | null][] = [
      ["groot", actiefGroot],
      ["normaal", actiefNormaal],
    ];
    for (const [baan, actief] of paren) {
      if (!actief || lopendeIdRef.current[baan] === actief.id) continue;
      lopendeIdRef.current[baan] = actief.id;
      void draaiBatch(baan, actief);
    }
  }, [actiefGroot, actiefNormaal, draaiBatch]);

  // Gepauzeerd: zodra er weer een gebruiker is (inlog in dit of een ander
  // tabblad) gaan de wachtende lussen verder; tot die tijd af en toe vragen.
  const gepauzeerd = state.gepauzeerd;
  useEffect(() => {
    if (!gepauzeerd) return;
    if (user) {
      dispatch({ type: "HERVAT" });
      const wachtend = hervattersRef.current;
      hervattersRef.current = [];
      wachtend.forEach((verder) => verder());
      return;
    }
    const timer = window.setInterval(() => {
      void vernieuwSessie();
    }, SESSIE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [gepauzeerd, user, vernieuwSessie]);

  // Tabblad sluiten of herladen (ook de inlogredirect) breekt de upload af:
  // dan eerst de browser laten vragen of dat de bedoeling is.
  const bezig = isBezig(state);
  useEffect(() => {
    if (!bezig) return;
    const waarschuw = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", waarschuw);
    return () => window.removeEventListener("beforeunload", waarschuw);
  }, [bezig]);

  // Na inloggen of herladen: wat in het register nog open staat.
  useEffect(() => {
    if (user) void zoekOnderbroken();
  }, [user, zoekOnderbroken]);

  // Netwerk terug: opnieuw kijken, want zonder netwerk gaf het register geen
  // antwoord en bleef de melding weg (L13).
  useEffect(() => {
    if (!user) return;
    const weerOnline = () => void zoekOnderbroken();
    window.addEventListener("online", weerOnline);
    return () => window.removeEventListener("online", weerOnline);
  }, [user, zoekOnderbroken]);

  // Een 401 van welke route ook terwijl er geüpload wordt: de wachtrij wacht
  // (de Graph-PUT's lopen door; ze hebben de sessie van de webapp niet nodig).
  useEffect(() => {
    const opVerlopen = () => {
      if (isBezig(stateRef.current)) dispatch({ type: "PAUZE" });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, opVerlopen);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, opVerlopen);
  }, []);

  const zetMelding = useCallback(
    (batchId: string, melding: string) =>
      dispatch({
        type: "ONDERBROKEN",
        lijst: stateRef.current.onderbroken.map((o) =>
          o.batchId === batchId ? { ...o, melding } : o,
        ),
      }),
    [],
  );

  const hervatOnderbroken = useCallback(
    async (batchId: string) => {
      const o = stateRef.current.onderbroken.find((x) => x.batchId === batchId);
      if (!o) return;
      // Loopt hij intussen in een ander tabblad, dan stil terugtrekken: de
      // melding hier klopt niet meer (B1b-2).
      const elders = () => dispatch({ type: "ONDERBROKEN_WEG", batchId });
      if (looptElders(batchId)) return elders();
      // Eerst de kiezer: de browser opent hem alleen direct na de klik.
      const gekozen = await kiesMap();
      if (!gekozen) return;
      if (looptElders(batchId)) return elders();
      // Claimen, en na het opvragen controleren dat de claim nog van ons is:
      // de laatste schrijver wint, de ander trekt zich terug.
      neemOver(batchId);
      try {
        const stand = await metSessie(() =>
          haalUploadBatch(o.dossierKenmerk, batchId),
        );
        if (!hartslagVanDitTabblad(batchId)) {
          serverIdsRef.current.delete(batchId);
          return elders();
        }
        const { items, nietGevonden, reden } = await koppelAanGekozenMap(
          stand,
          gekozen,
          planCdZip,
        );
        if (items.length === 0) {
          laatLos(batchId);
          zetMelding(
            batchId,
            reden === "meerdere_cd_mappen"
              ? "Er staan meerdere cd-mappen met deze naam in deze map. Kies de cd-map zelf."
              : "Deze map hoort niet bij de onderbroken upload. Kies de map die je toen uploadde.",
          );
          return;
        }
        for (const b of nietGevonden) {
          void werkBestandBij(o.dossierKenmerk, batchId, b.id, {
            status: "MISLUKT",
            fout: "Niet gevonden in de gekozen map",
          }).catch(() => undefined);
        }
        dispatch({
          type: "ENQUEUE",
          batch: {
            id: volgendeIdRef.current++,
            dossierKenmerk: o.dossierKenmerk,
            items,
            batchId,
            aangemaaktOp: stand.aangemaaktOp,
          },
        });
      } catch (err) {
        laatLos(batchId);
        zetMelding(batchId, maakFoutmelding(err).hoofdtekst);
      }
    },
    [metSessie, zetMelding, neemOver, laatLos],
  );

  const stopOnderbroken = useCallback(
    async (batchId: string) => {
      const o = stateRef.current.onderbroken.find((x) => x.batchId === batchId);
      if (!o) return;
      // Bezit een ander tabblad hem nog, dan niet stoppen: dat zou zijn lopende
      // upload de sessie afnemen. Stil terugtrekken (B1b-2).
      if (looptElders(batchId)) {
        dispatch({ type: "ONDERBROKEN_WEG", batchId });
        return;
      }
      await metSessie(() => stopUploadBatch(o.dossierKenmerk, batchId)).catch(
        () => undefined,
      );
      vergeetBatch(o.dossierKenmerk, batchId);
      dispatch({ type: "ONDERBROKEN_WEG", batchId });
    },
    [metSessie],
  );

  const value = useMemo<UploadQueueContextValue>(
    () => ({
      lopend: BANEN.flatMap((baan) => {
        const actief = state.banen[baan].actief;
        return actief
          ? [
              {
                baan,
                dossierKenmerk: actief.dossierKenmerk,
                done: actief.done,
                total: actief.items.length,
                current: actief.current,
                currentFraction: actief.currentFraction,
                errors: actief.errors,
              },
            ]
          : [];
      }),
      wachtrij: BANEN.flatMap((baan) =>
        state.banen[baan].wachtrij.map((b) => ({
          id: b.id,
          baan,
          dossierKenmerk: b.dossierKenmerk,
          aantal: b.items.length,
        })),
      ),
      gepauzeerd: state.gepauzeerd,
      lastCompleted: state.lastCompleted,
      onderbroken: state.onderbroken,
      hervatOnderbroken,
      stopOnderbroken,
      startBatchUpload,
    }),
    [state, startBatchUpload, hervatOnderbroken, stopOnderbroken],
  );

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
    </UploadQueueContext.Provider>
  );
};

export function useUploadQueue(): UploadQueueContextValue {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) {
    throw new Error(
      "useUploadQueue moet binnen een UploadQueueProvider gebruikt worden",
    );
  }
  return ctx;
}
